import type { QueryClient } from "@tanstack/react-query";
import { readActiveSectorId } from "@/core/api/activeSector";
import {
  AuthenticatedAppProviders,
  ChatsPage,
  MainLayout,
  OpenConversation,
} from "@/core/boot/shellChunks";
import { sectorPermissionsQueryOptions } from "@/core/auth/useSectorPermissions";
import { sectorAccessStateQueryOptions } from "@/core/hooks/useSectorAccessState";
import { sectorDetailQueryOptions } from "@/core/providers/UserSectorProvider/sectorDetailQuery";
import { DEFAULT_CHAT_TAB_LISTS } from "@/features/chat/hooks/useChatTabLists";
import { closedChatsCountQueryOptions } from "@/features/chat/hooks/useClosedChats";
import { openChatsCountQueryOptions } from "@/features/chat/hooks/useOpenChatsCount";
import { accountAccessesQueryOptions } from "@/hooks/useAccountAccesses";
import { chatListQueryOptions } from "@/hooks/useChats";
import { userMeQueryOptions } from "@/shared/providers/AuthProvider/userMeQuery";

/** O token como o `AuthProvider` grava (`useLocalStorage` serializa em JSON). */
export function readStoredAuthToken(): string | null {
  try {
    const raw = localStorage.getItem("auth");
    if (!raw) return null;
    const token = (JSON.parse(raw) as { token?: unknown })?.token;
    return typeof token === "string" && token ? token : null;
  } catch {
    return null;
  }
}

const isChatsPath = (pathname: string) =>
  pathname === "/chats" || pathname.startsWith("/chats/");

/**
 * Com sessão guardada, baixa já os chunks do shell autenticado (e da tela de
 * conversas em `/chats`). Antes eles só começavam no `requestIdleCallback` do
 * `AppRoutes` e o primeiro nível de API esperava o chunk dos providers
 * (~300 ms medidos no build de produção com rede 4G). Pré-carregados, eles
 * também não suspendem ao renderizar (ver `lazyWithPreload`).
 */
export function preloadShellChunks(pathname: string) {
  if (!readStoredAuthToken()) return;
  // Falha aqui não é erro: o render tenta de novo (ver `lazyWithPreload`).
  const ignore = () => {};
  AuthenticatedAppProviders.preload().catch(ignore);
  MainLayout.preload().catch(ignore);
  if (isChatsPath(pathname)) ChatsPage.preload().catch(ignore);
  // F5 com uma conversa aberta: ela aparece junto com a lista (SA-1474).
  if (pathname.startsWith("/chats/")) OpenConversation.preload().catch(ignore);
}

/**
 * Pré-carrega, antes do React montar, o que o F5 vai pedir (SA-1452).
 *
 * Sem isto a tela de conversas esperava três rodadas de API em série:
 * `/user/me` → (setor, acessos, estado de acesso) → listas, porque cada camada
 * de provider e gate só monta a seguinte quando a anterior responde. Aqui tudo
 * sai junto, com as MESMAS `queryKey`/`queryFn` dos hooks (as fábricas), e os
 * hooks, quando montam, encontram a request em voo ou a resposta em cache.
 *
 * Usa o setor guardado no localStorage, como as listas já faziam (SA-1277: só
 * detalhe e timeline esperam a lista de acessos). Se o acesso a ele foi
 * revogado, as listas levam 403, o interceptor refaz acessos e permissões e o
 * provider troca de setor, como sem o pré-carregamento. As listas só saem em
 * `/chats`; a tela as mostra só com o setor conectado (`useSectorConnection`).
 */
export function prefetchOnBoot(queryClient: QueryClient, pathname: string) {
  const token = readStoredAuthToken();
  if (!token) return;
  const sectorId = readActiveSectorId();

  void queryClient.prefetchQuery(userMeQueryOptions(token));
  void queryClient.prefetchQuery(accountAccessesQueryOptions(token));
  void queryClient.prefetchQuery(
    sectorPermissionsQueryOptions(sectorId, token)
  );
  if (!sectorId) return;

  void queryClient.prefetchQuery(sectorDetailQueryOptions(sectorId, token));
  void queryClient.prefetchQuery(
    sectorAccessStateQueryOptions(sectorId, token)
  );
  void queryClient.prefetchQuery(
    openChatsCountQueryOptions({ sectorId, token })
  );
  void queryClient.prefetchQuery(
    closedChatsCountQueryOptions({ sectorId, token })
  );
  if (!isChatsPath(pathname)) return;

  for (const list of DEFAULT_CHAT_TAB_LISTS) {
    void queryClient.prefetchInfiniteQuery(
      chatListQueryOptions({ ...list, sectorId, token, queryClient })
    );
  }
}
